import { useId, type ComponentProps, type ReactNode } from 'react';
import { AlertCircle, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../cn.js';

// ------------------------------------------------------------------ Button

type ButtonVariant = 'primary' | 'secondary' | 'glass' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const buttonVariants: Record<ButtonVariant, string> = {
  // Solid brand with a top highlight and a soft colored drop: the one loud element on a screen.
  primary:
    'bg-brand-solid text-brand-fg shadow-[inset_0_1px_0_rgb(255_255_255/0.22),0_1px_2px_rgb(16_24_40/0.12),0_6px_16px_-6px_color-mix(in_srgb,var(--ui-brand-solid)_55%,transparent)] ' +
    'hover:brightness-[1.06] hover:shadow-[inset_0_1px_0_rgb(255_255_255/0.22),0_2px_4px_rgb(16_24_40/0.12),0_10px_22px_-8px_color-mix(in_srgb,var(--ui-brand-solid)_60%,transparent)]',
  secondary: 'relative material text-fg hover:bg-[var(--control-bg-hover)] [&::before]:opacity-70',
  glass: 'relative glass-thin text-fg hover:bg-[var(--glass-2)]',
  danger:
    'bg-danger-solid text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_6px_16px_-8px_color-mix(in_srgb,var(--ui-danger-solid)_60%,transparent)] hover:brightness-[1.06]',
  ghost: 'text-fg hover:bg-[var(--control-bg-hover)]',
};

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm gap-1.5',
  md: 'h-11 px-5 text-sm gap-2',
  lg: 'h-13 px-6 text-base gap-2.5',
  icon: 'size-11 p-0',
};
// Icon-only buttons are square; they must not inherit the text padding, which would squeeze the icon.
const iconButtonSizes: Record<ButtonSize, string> = {
  sm: 'size-9',
  md: 'size-11',
  lg: 'size-13',
  icon: 'size-11',
};

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  className,
  children,
  disabled,
  type,
  ...rest
}: ButtonProps) {
  const iconOnly = !children || size === 'icon';
  return (
    <button
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-control font-semibold whitespace-nowrap select-none',
        'pressable lift tap-target focus-ring',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none',
        iconOnly ? iconButtonSizes[size] : buttonSizes[size],
        buttonVariants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

// ------------------------------------------------------------------ Inputs

/**
 * Shared control look. 16px text on phones so iOS never zooms on focus; 14px from sm up.
 * Error is shown by border + ring here and by an icon + message in <Field> (never color alone).
 */
export const controlClass =
  'ui-control w-full rounded-control border border-transparent bg-[var(--control-bg)] px-3.5 text-base text-fg ' +
  'placeholder:text-subtle transition-[background-color,border-color,box-shadow] duration-[var(--dur-2)] ' +
  'hover:bg-[var(--control-bg-hover)] sm:text-sm ' +
  'focus:border-brand focus:bg-surface focus:shadow-[0_0_0_4px_var(--focus-ring)] focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:bg-[var(--control-bg)] ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:shadow-[0_0_0_4px_color-mix(in_srgb,var(--ui-danger)_25%,transparent)]';

export function Input({ className, ...rest }: ComponentProps<'input'>) {
  return <input className={cn(controlClass, 'h-12 sm:h-11', className)} {...rest} />;
}

export function Textarea({ className, rows = 3, ...rest }: ComponentProps<'textarea'>) {
  return (
    <textarea
      rows={rows}
      className={cn(controlClass, 'min-h-24 resize-y py-3 leading-relaxed', className)}
      {...rest}
    />
  );
}

export function Select({ className, children, ...rest }: ComponentProps<'select'>) {
  return (
    <div className="relative w-full">
      <select
        className={cn(controlClass, 'ui-select h-12 cursor-pointer pe-10 sm:h-11', className)}
        {...rest}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute end-3.5 top-1/2 size-4 -translate-y-1/2 text-muted"
        aria-hidden
      />
    </div>
  );
}

/** Search field with leading icon, optional busy indicator and a clear button. */
export function SearchInput({
  className,
  value,
  onValueChange,
  loading,
  clearLabel,
  ...rest
}: Omit<ComponentProps<'input'>, 'value' | 'type'> & {
  value: string;
  onValueChange: (v: string) => void;
  loading?: boolean;
  clearLabel?: string;
}) {
  return (
    <div className={cn('relative w-full', className)}>
      <Search
        className="pointer-events-none absolute start-3.5 top-1/2 size-4 -translate-y-1/2 text-muted"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className={cn(controlClass, 'h-12 ps-10 pe-11 sm:h-11')}
        {...rest}
      />
      <span className="absolute end-1.5 top-1/2 flex -translate-y-1/2 items-center">
        {loading ? (
          <Loader2 className="m-2.5 size-4 animate-spin text-muted" aria-hidden />
        ) : value ? (
          <button
            type="button"
            onClick={() => onValueChange('')}
            className="pressable focus-ring tap-target grid size-8 place-items-center rounded-full text-muted hover:bg-[var(--control-bg-hover)] hover:text-fg"
            aria-label={clearLabel ?? 'Clear'}
          >
            <X className="size-4" />
          </button>
        ) : null}
      </span>
    </div>
  );
}

export function Checkbox({
  label,
  className,
  ...rest
}: ComponentProps<'input'> & { label: ReactNode }) {
  return (
    <label
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center gap-3 text-sm has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-55',
        className,
      )}
    >
      <input
        type="checkbox"
        className="size-5 shrink-0 cursor-pointer rounded-md accent-[var(--ui-brand-solid)]"
        {...rest}
      />
      <span>{label}</span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}

/** iOS-style switch with a springy thumb. role=switch, label-clickable. */
export function Switch({ checked, onChange, label, description, disabled, id }: SwitchProps) {
  const autoId = useId();
  const switchId = id ?? autoId;
  return (
    <div className="flex min-h-11 items-center justify-between gap-4">
      {label && (
        <label
          htmlFor={switchId}
          className={cn(
            'min-w-0 cursor-pointer text-sm',
            disabled && 'cursor-not-allowed opacity-55',
          )}
        >
          <span className="font-medium">{label}</span>
          {description && <span className="mt-0.5 block text-muted">{description}</span>}
        </label>
      )}
      <button
        id={switchId}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-[30px] w-[50px] shrink-0 items-center rounded-full p-[2px] focus-ring',
          // 44px hit area without changing the visual size.
          "after:absolute after:-inset-x-1 after:-inset-y-[7px] after:content-['']",
          'transition-colors duration-[var(--dur-2)] disabled:cursor-not-allowed disabled:opacity-45',
          'shadow-[inset_0_0_0_1px_var(--ui-line)]',
          checked ? 'bg-brand-solid' : 'bg-surface-3',
        )}
      >
        <span
          className={cn(
            'block size-[26px] rounded-full bg-white shadow-[0_2px_6px_rgb(0_0_0/0.18),0_0_0_0.5px_rgb(0_0_0/0.04)]',
            'transition-transform duration-[var(--dur-3)] ease-spring',
            checked ? 'translate-x-5 rtl:-translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: (props: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby'?: string;
  }) => ReactNode;
  className?: string;
}

/** Label + control + hint/error with correct ARIA wiring. Errors carry an icon, not only color. */
export function Field({ label, hint, error, children, className }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-fg">
        {label}
      </label>
      {children({ id, 'aria-invalid': Boolean(error), 'aria-describedby': describedBy })}
      {error ? (
        <p id={`${id}-err`} role="alert" className="flex items-start gap-1.5 text-xs text-danger">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function ColorInput({
  value,
  onChange,
  id,
  ...rest
}: { value: string; onChange: (v: string) => void; id?: string } & Omit<
  ComponentProps<'input'>,
  'value' | 'onChange'
>) {
  const swatch = /^#[0-9a-fA-F]{6}/.test(value) ? value.slice(0, 7) : '#000000';
  return (
    <div className="flex items-center gap-2">
      <span
        className="relative size-12 shrink-0 overflow-hidden rounded-control shadow-[inset_0_0_0_1px_var(--ui-line-strong)] sm:size-11"
        style={{ background: swatch }}
      >
        <input
          type="color"
          aria-hidden
          tabIndex={-1}
          value={swatch}
          disabled={rest.disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </span>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="ltr-iso font-mono uppercase"
        maxLength={9}
        {...rest}
      />
    </div>
  );
}

// ------------------------------------------------------------------ Badge

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';
const tones: Record<Tone, string> = {
  neutral: 'bg-surface-3/70 text-muted',
  brand: 'bg-brand-soft text-brand',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
  dot = tone !== 'neutral' && tone !== 'brand',
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  /** Leading status dot; on by default for status tones. */
  dot?: boolean;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

// ------------------------------------------------------------------ Surfaces

/** Card surface: translucent material with rim light, no blur (see glass.css). */
export function Card({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn('material relative min-w-0 animate-rise rounded-card', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5 sm:px-6 sm:pt-6">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold text-fg">{title}</h2>}
            {description && <p className="mt-1 text-sm text-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn('p-5 sm:p-6', (title || actions) && 'pt-4 sm:pt-5', bodyClassName)}>
        {children}
      </div>
    </section>
  );
}

/** Floating glass container (real blur) for overlays and sticky chrome. */
export function GlassPanel({
  className,
  strength = 'regular',
  children,
  ...rest
}: ComponentProps<'div'> & { strength?: 'thin' | 'regular' | 'thick' }) {
  return (
    <div
      className={cn(
        'relative rounded-panel',
        strength === 'thin' ? 'glass-thin' : strength === 'thick' ? 'glass-thick' : 'glass',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Small inset tile inside a card (codes, previews, grouped rows). */
export function Tile({ className, ...rest }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-lg bg-[var(--control-bg)] shadow-[inset_0_0_0_1px_var(--ui-line)]',
        className,
      )}
      {...rest}
    />
  );
}

// ------------------------------------------------------------------ Loading

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      className={cn('flex items-center justify-center gap-2.5 py-12 text-muted', className)}
    >
      <Loader2 className="size-5 animate-spin" aria-hidden />
      {label ? <span className="text-sm">{label}</span> : <span className="sr-only">…</span>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4', className)} aria-hidden />;
}

/** Placeholder rows shaped like a table/list while data loads. */
export function TableSkeleton({ rows = 5, label }: { rows?: number; label?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" className="space-y-3 py-2">
      <span className="sr-only">{label ?? t('common.loading')}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className={cn('h-3.5', ['w-3/5', 'w-4/5', 'w-1/2', 'w-2/3'][i % 4])} />
            <Skeleton className="h-3 w-2/5" />
          </div>
          <Skeleton className="hidden h-6 w-20 rounded-full sm:block" />
        </div>
      ))}
    </div>
  );
}

/** Whole-page placeholder: header, stat row and a content card. */
export function PageSkeleton({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" className="animate-fade space-y-6">
      <span className="sr-only">{label ?? t('common.loading')}</span>
      <div className="space-y-3">
        <Skeleton className="h-8 w-56 rounded-md" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="material relative space-y-3 rounded-card p-5">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-8 w-16 rounded-md" />
          </div>
        ))}
      </div>
      <div className="material relative space-y-4 rounded-card p-6">
        <Skeleton className="h-5 w-40" />
        <TableSkeleton rows={4} />
      </div>
    </div>
  );
}
